import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, updateDoc, doc, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useConfig } from '../contexts/ConfigContext';
import LoadingAnimation from './LoadingAnimation';

const AdminPrayers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const config = useConfig();

  const [loading, setLoading] = useState(true);
  const [prayers, setPrayers] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login', { state: { from: location.pathname } });
      } else if (config.configLoaded && config.adminEmails.length > 0 && !config.adminEmails.includes(user.email)) {
        navigate('/');
      } else if (config.configLoaded) {
        fetchPrayers().then(() => setLoading(false));
      }
    });
    return () => unsubscribe();
  }, [config.configLoaded, config.adminEmails]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPrayers = async () => {
    const snap = await getDocs(query(collection(db, 'prayers'), orderBy('name', 'asc')));
    setPrayers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prayer? It will be hidden from the website.')) return;
    await updateDoc(doc(db, 'prayers', id), { status: 'deleted' });
    await fetchPrayers();
  };

  const handleRestore = async (id) => {
    await updateDoc(doc(db, 'prayers', id), { status: 'published' });
    await fetchPrayers();
  };

  const visiblePrayers = showDeleted ? prayers : prayers.filter(p => p.status !== 'deleted');

  if (loading) return <div style={{ minHeight: '100vh' }}><LoadingAnimation /></div>;

  return (
    <div className="stt-admin-page">
      <a href="/admin" className="stt-admin-back">← Back to Admin</a>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
        <h1 className="stt-admin-title">Prayers</h1>
        <a href="/admin/prayers/new" className="btn stt-btn-primary">+ New Prayer</a>
      </div>
      <hr />

      <div className="stt-admin-card">
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <label style={{ fontSize: '0.82rem', color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: 0 }}>
            <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
            Show deleted
          </label>
        </div>

        {visiblePrayers.length === 0 ? (
          <p style={{ color: '#888' }}>No prayers yet. Click "+ New Prayer" to add one.</p>
        ) : (
          <div className="stt-admin-list">
            {visiblePrayers.map(p => (
              <div key={p.id} className={`stt-admin-list-row${p.status === 'deleted' ? ' deleted' : ''}`}>
                <div className="stt-admin-list-info">
                  <div className="stt-admin-list-title">
                    <a href={`/prayers/${p.id}`} target="_blank" rel="noopener noreferrer">
                      {p.name}
                    </a>
                  </div>
                  {(p.categories || []).length > 0 && (
                    <div className="stt-admin-list-meta">
                      {p.categories.map(cat => (
                        <span key={cat} className="stt-prayer-tag">{cat}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="stt-admin-list-actions">
                  <a href={`/admin/prayers/${p.id}/edit`} className="btn btn-sm btn-outline-secondary">Edit</a>
                  {p.status === 'deleted' ? (
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => handleRestore(p.id)}>Restore</button>
                  ) : (
                    <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(p.id)}>Delete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPrayers;
